import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Input from "../ui/Input";
import Button from "../ui/Button";
import API_BASE_URL from "../Config/config";
import { useSubscription } from "../hooks/useSubscription";
import UpgradeScreen from "./access/UpgradeScreen";
import PremiumModal from "./access/PremiumModal";

const TSGPromptPage = ({ mode = "learner" }) => {
  const { intent } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [chat, setChat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const chatEndRef = useRef(null);

  const { isPaid, canAccessFeature, plan, isLoaded } = useSubscription();
  const isTenantMode = mode === "tenant";

  const handleAsk = async () => {
    if (!input.trim()) return;

    if (!isTenantMode) {
      // TSG AI access strictly for 3/6 month subscribers
      if (!canAccessFeature('TSG_AI')) {
        setShowUpgradeModal(true);
        return;
      }
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    // NextGen Builder accounts don't have `.userId`/`.user_id` — fall back to
    // the namespaced pseudo-identity written to localStorage by
    // NextGenAuthContext.jsx, same as ExplainPage.jsx already does.
    const userId = user?.userId || user?.user_id || localStorage.getItem("user_id");

    if (!userId) {
      alert("Please log in to use TSG.");
      return;
    }

    setLoading(true);

 try {
 const res = await axios.post(`${API_BASE_URL}/api/tsg/${intent}`, {
 userId,
 prompt: input,
 chatHistory: chat,
 language:"en",
 technology:"general",
 topic:"general",
 });

 const reply = res.data?.reply ||"No reply.";
 setChat((prev) => [
 ...prev,
 { role:"user", content: input },
 { role:"assistant", content: reply },
 ]);
 setInput("");
 } catch (err) {
 setChat((prev) => [
 ...prev,
 { role:"assistant", content:"❌ Error reaching TSG." },
 ]);
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
    const container = chatEndRef.current?.parentElement;
    if (container) {
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      if (isAtBottom || chat.length <= 2) {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [chat]);

  return (
    <div className="p-4 md:p-8 min-h-screen bg-white text-gray-900">
      <div className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-120px)]">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-purple/10 rounded-xl border border-primary-purple/20">
              <svg className="w-8 h-8 text-primary-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl font-extrabold text-primary-purple">
                @tsg → <span className="capitalize">{intent.replace("_", " ")}</span>
              </h2>
              <p className="text-gray-500 text-sm">Personal AI Assistant for Tech Mastery</p>
            </div>
          </div>
        </div>

        {/* Chat Messages Area */}
        <div className="flex-1 overflow-y-auto mb-6 pr-2 custom-scrollbar space-y-6">
          {chat.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4 opacity-60">
              <div className="w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center border border-primary-purple/20">
                <svg className="w-10 h-10 text-primary-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <p className="text-xl font-medium text-gray-700">Ready to help with your {intent.replace("_", " ")}</p>
                <p className="text-sm text-gray-500">Ask anything and TSG will provide professional insights.</p>
              </div>
            </div>
          ) : (
            chat.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2 duration-300`}
              >
                <div
                  className={`max-w-[85%] md:max-w-[75%] p-4 rounded-2xl shadow-sm transition-all ${
                    msg.role === "user"
                      ? "bg-primary-purple text-white rounded-tr-none shadow-purple-100"
                      : "bg-white border border-gray-100 text-gray-800 rounded-tl-none shadow-md"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-bold uppercase tracking-wider ${msg.role === "user" ? "text-purple-100" : "text-primary-purple"}`}>
                      {msg.role === "user" ? "You" : "TSG Assistant"}
                    </span>
                  </div>
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">
                    {msg.content}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Sticky Input Area */}
        <div className="relative">
          {loading && (
            <div className="absolute -top-10 left-4 flex items-center gap-2 text-primary-purple text-sm font-medium">
              <span className="flex space-x-1">
                <span className="w-1.5 h-1.5 bg-primary-purple rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                <span className="w-1.5 h-1.5 bg-primary-purple rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                <span className="w-1.5 h-1.5 bg-primary-purple rounded-full animate-bounce"></span>
              </span>
              TSG is thinking...
            </div>
          )}
          <div className="flex items-center gap-3 p-2 bg-white rounded-2xl border border-primary-purple/20 shadow-lg focus-within:border-primary-purple transition-all">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleAsk()}
              placeholder={`Ask about ${intent.replace("_", " ")}...`}
              className="flex-1 bg-transparent px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none"
            />
            <button
              onClick={handleAsk}
              disabled={loading || !input.trim()}
              className="p-3 rounded-xl bg-primary-purple text-white hover:bg-opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed group shadow-md"
            >
              <svg className={`w-6 h-6 transform ${loading ? 'animate-spin' : 'group-hover:translate-x-1 group-hover:-translate-y-1'} transition-transform`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      <PremiumModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="TSG AI - Premium Feature"
        message="TSG AI is available only for 3 and 6 month subscribers. Upgrade now to unlock AI-powered assistance."
      >
        <UpgradeScreen feature="TSG_AI" />
      </PremiumModal>
    </div>
  );
};

export default TSGPromptPage;



